// Rules for opening and editing a row: its page URL and category, who may
// open the page (the title link and the "⋯" entry), the edit mode, the
// signing-request lock and moves in the document tree. Pure functions; the
// signing actions and the order they are offered in live in file-row-model.ts.
import type { FileItem } from "./file-item";
import { type RowContext, templateIdOf } from "./file-row-model";

/**
 * The document's category for its page URL and its signing modal. A child
 * filed without its own category falls back to the parent document's.
 */
export function getRowCategoryId(item: FileItem, parentCategoryId: string): string | null {
  return item.categories?.[0]?.id || parentCategoryId || null;
}

/**
 * The document's own page, or null when the row knows no category to put in
 * the URL (the page itself resolves the file by id; the category is only
 * navigation).
 */
export function getFileViewUrl(
  item: FileItem,
  companyId: string | undefined,
  parentCategoryId: string
): string | null {
  const categoryId = getRowCategoryId(item, parentCategoryId);
  return categoryId && companyId
    ? `/app/${companyId}/documents/${categoryId}/${item.id}`
    : null;
}

/** Same roles as the document page's own route guard. */
export function canOpenDocumentPage(permissionGroups: string[] | undefined): boolean {
  const groups = permissionGroups || [];
  return ["Master", "SalesMan", "Access document"].some((group) => groups.includes(group));
}

/**
 * Whether the row may be moved to / detached from a parent document.
 * files/fetch-by-id returns `isModuleManaged`; other payloads mirror its three
 * checks (an order document, a system template, an amendment).
 */
export function isModuleManagedRow(item: FileItem): boolean {
  if (item.isModuleManaged != null) return Boolean(item.isModuleManaged);
  return (
    Boolean(item.order) ||
    Boolean(item.systemTemplateKey) ||
    (item.subNumber != null && Number(item.subNumber) > 0)
  );
}

/**
 * "Redaguoti": a template-built unsigned document re-opens its template form
 * (when a customer is known), anything else opens the plain file form.
 */
export function getEditMode(
  item: FileItem,
  ctx: RowContext & { routeCustomerId: string | undefined; isEditable: boolean }
): { mode: "template" | "file"; disabled: boolean } {
  const isFinanceDocument =
    typeof item.templateId === "object" && item.templateId?.subject === "finance";
  const useTemplateForm = Boolean(
    (templateIdOf(item) || ctx.isOrderAmendment) &&
      (ctx.customerId || ctx.routeCustomerId) &&
      !item.isSigned
  );
  if (useTemplateForm) return { mode: "template", disabled: isFinanceDocument };
  return { mode: "file", disabled: Boolean(item.isSigned) || !ctx.isEditable };
}

/**
 * "Redaguoti pasirašymo užklausą" re-posts the whole signer list. Once someone
 * signed a Click document, its signers can only be added / removed one by one
 * on its page, so the menu entry is shown disabled with that explanation.
 */
export function isEditRequestLocked(item: FileItem): boolean {
  return (
    item.signType === "Click" &&
    (item.signersList || []).some(
      (signer) => signer.status === "signed" || Boolean(signer.dokobitmeta)
    )
  );
}
